/*
 * BETENOIR gift · free 1.5 ml miniatures in the cart (markup: snippets/bn-cart-gifts.liquid).
 * The mini product is priced €0 in Shopify; this file keeps the gift lines right: one _bn_gift "same" mini per
 * eligible fragrance (product metafield betenoir.mini) when D.s, up to D.c optional "choice" minis (each a
 * different fragrance), quantity 1, bottles never touched. D.s / D.c come from Theme settings (bn_gift_same / _choice).
 * One pass at a time, a few sequential requests, one morph.
 * Also: a La Découverte voucher typed with I / L / O is sent as 1 / 0 (near the end).
 */
import { CartErrorEvent } from '@shopify/events';
import { CartSectionRestoredEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const D = JSON.parse(document.getElementById('BnGiftData').textContent);
// Theme is a global const of snippets/scripts.liquid, not a property of window: its routes carry the locale (/en/…)
const R = (typeof Theme !== 'undefined' && Theme.routes) || {};
const avail = new Set(D.a);
const sheet = document.head.appendChild(document.createElement('style'));
const CARTS = 'cart-items-component[data-section-id]';
let mem = load();
let want = 0; // what the customer just picked: { from: the chosen mini it replaces (0 = open slot), to }
let picking = false;
let last = null; // latest full list of cart lines
let running = null;
let again = false;
let done = 0;
let bypass = false;

function load() {
  try {
    const m = JSON.parse(sessionStorage.getItem('bn-gift'));
    if (Array.isArray(m?.m)) return m;
  } catch {}
  return { m: [] };
}
function save() {
  try {
    sessionStorage.setItem('bn-gift', JSON.stringify(mem));
  } catch {}
}

const isMini = (i) => i.product_id === D.p;

/**
 * A row reserved (data-state="pending") for a mini the cart already holds shows it. Horizon's own re-render of the cart
 * can land after ours, from a response written before the mini was added: nothing would morph the row back, and it
 * stayed an empty gap (seen once in about twenty adds, 10-31).
 */
function heal() {
  if (!last) return;
  document.querySelectorAll('.bn-gift[data-state="pending"]').forEach((row) => {
    const same = Number(row.dataset.bnGift);
    const from = Number(row.dataset.bnGiftFrom);
    const kind = same ? 'same' : 'choice';
    const id = same || from;
    if (last.some((i) => isMini(i) && i.variant_id === id && i.properties?._bn_gift === kind && i.final_line_price === 0)) {
      row.dataset.state = 'in';
    }
  });
}
new MutationObserver(heal).observe(document.documentElement, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['data-state'],
});
/** Bottles in the cart (minis left out), or undefined when `items` is not the whole cart. */
const count = (items, cart) =>
  Array.isArray(items) && (!cart?.lines || cart.lines.length === items.length)
    ? items.reduce((n, i) => n + (isMini(i) ? 0 : i.quantity), 0)
    : undefined;

/** What to remove ({key: qty}) and add, or null when the cart is right. */
function plan(items) {
  last = items;
  const elig = [];
  const sig = [];
  for (const i of items) {
    const m = !isMini(i) && D.m[i.variant_id];
    if (!m) continue;
    sig.push(`${i.variant_id}:${i.quantity}`);
    if (!elig.includes(m)) elig.push(m);
  }
  const s = sig.sort().join();
  if (mem.s !== s) (mem = { s, m: [] }), save();
  if (!elig.length) want = 0;

  const rm = {};
  const add = [];
  const same = new Set();
  const chosen = [];
  for (const i of items) {
    if (!isMini(i)) continue;
    const g = i.properties?._bn_gift;
    const v = i.variant_id;
    if (g === 'same' && D.s && elig.includes(v) && !mem.m.includes(v) && !same.has(v)) same.add(v);
    else if (g === 'choice' && elig.length && chosen.length < D.c && !chosen.includes(v) && want?.from !== v) chosen.push(v);
    else {
      rm[i.key] = 0; // foreign, extra, orphan, swapped or held back
      continue;
    }
    if (i.quantity !== 1) rm[i.key] = 1;
  }
  if (want && (chosen.includes(want.to) || chosen.length >= D.c)) want = 0;
  for (const v of D.s ? elig : []) {
    if (!same.has(v) && !mem.m.includes(v) && avail.has(v)) add.push({ id: v, quantity: 1, properties: { _bn_gift: 'same' } });
  }
  if (want) add.push({ id: want.to, quantity: 1, properties: { _bn_gift: 'choice' } });
  hide();
  return add.length || Object.keys(rm).length ? { rm, add } : null;
}

/** Same-fragrance minis that are sold out leave the band at once. */
function hide() {
  const sel = mem.m.map((v) => `#BnGift-${v}`);
  sheet.textContent = sel.length ? `${sel.join()}{display:none!important}` : '';
}

async function json(res) {
  const body = await res.json();
  if (!res.ok || body.status) throw Object.assign(new Error(body.description || body.message || res.statusText), { code: res.status });
  return body;
}
const sections = () => [...document.querySelectorAll(CARTS)].map((c) => c.dataset.sectionId);
const post = (url, body) =>
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: sections().join(), sections_url: location.pathname }),
  }).then(json);
// While gift lines move, Horizon's line numbers are stale: hold the cart like Horizon does during its own updates.
const busy = (on) => {
  document.querySelectorAll('[ref="cartTotal"]').forEach((t) => t.toggleAttribute('shimmer', on));
  document.querySelectorAll(CARTS).forEach((c) => c.classList.toggle('cart-items-disabled', on));
};

async function pass(items) {
  let html;
  let err;
  const pick = picking && want;
  picking = false;
  try {
    for (let n = 0; n < 6; n++) {
      items ||= (await fetch(`${R.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then(json)).items;
      const p = plan(items);
      if (!p) break;
      busy(true);
      items = null;
      if (Object.keys(p.rm).length) {
        ({ items, sections: html } = await post(`${R.cart_update_url || '/cart/update'}.js`, { updates: p.rm }));
        continue;
      }
      try {
        html = (await post(R.cart_add_url || '/cart/add.js', { items: p.add })).sections;
      } catch (e) {
        if (e.code !== 422) throw e;
        // Sold out meanwhile, or a limit: the chosen mini says so, a same-fragrance mini is skipped.
        for (const a of p.add) a.properties._bn_gift === 'same' ? mem.m.push(a.id) : ((want = 0), (err = e));
        save();
      }
    }
  } catch (e) {
    err = e;
  }
  if (html) await morph(html);
  busy(false);
  document.querySelectorAll('.bn-gift__picker[aria-busy]').forEach((p) => p.removeAttribute('aria-busy'));
  document.querySelectorAll('[data-bn-busy]').forEach((c) => c.removeAttribute('data-bn-busy'));
  if (err) fail(err, pick);
  heal();
  done = Date.now();
}

async function morph(html) {
  for (const c of document.querySelectorAll(CARTS)) {
    const id = c.dataset.sectionId;
    if (!html?.[id]) continue;
    try {
      await morphSection(id, html[id], { mode: c.dataset.drawer === undefined ? 'full' : 'hydration' });
      document.querySelector(`cart-items-component[data-section-id="${id}"]`)?.dispatchEvent(new CartSectionRestoredEvent());
    } catch (e) {
      console.warn('[bn-gift]', e);
    }
  }
  document.querySelectorAll('cart-quantity-selector-component').forEach((s) => s.updateButtonStates?.());
}

/** A failed pick shows its message in the slot it came from, or in the open slot once that mini is gone. */
function fail(e, pick) {
  console.warn('[bn-gift]', e);
  const slot = (from) => document.querySelector(`.bn-gift--choice[data-bn-gift-from="${from}"] [data-bn-gift-error]`);
  const box = pick ? slot(pick.from) || slot(0) : null;
  if (box) {
    box.querySelector('.cart-item__error-text').textContent = (e.code === 422 && e.message) || D.e;
    box.hidden = false;
  }
  (box || document).dispatchEvent(
    new CartErrorEvent({ error: e.message || D.e, code: e.code === 422 ? 'INVALID' : 'SERVICE_UNAVAILABLE' })
  );
}

/** One pass at a time; anything asked meanwhile becomes one more pass on a fresh cart. */
function reconcile(items) {
  if (running) {
    again = true;
    return running;
  }
  return (running = pass(items).finally(() => {
    running = null;
    if (again) (again = false), reconcile();
  }));
}

let preloaded = false;
function preload() {
  if (preloaded) return;
  preloaded = true;
  for (const u of D.i) if (u) new Image().src = u;
}

document.addEventListener('shopify:cart:lines-update', (e) => {
  preload();
  e.promise
    ?.then(({ cart, detail }) => reconcile(count(detail?.items, cart) == null ? null : detail.items))
    .catch(() => reconcile());
});
document.addEventListener('shopify:cart:discount-update', (e) => (e.promise || Promise.resolve()).finally(() => reconcile()));
document.addEventListener('theme-drawer:open', (e) => {
  if (e.target?.id !== 'cart-drawer') return;
  preload();
  if (!running && Date.now() - done > 1500) reconcile();
});
addEventListener('pageshow', (e) => e.persisted && reconcile());

document.addEventListener('click', (e) => {
  const t = e.target instanceof Element && e.target.closest('[data-bn-gift-pick],[data-bn-gift-change]');
  if (!t) return;
  const slot = t.closest('.bn-gift--choice');
  const picker = slot?.querySelector('.bn-gift__picker');
  if (!picker) return;
  const change = slot.querySelector('[data-bn-gift-change]');
  const toggle = (open) => {
    if (!change) return;
    picker.hidden = !open;
    change.setAttribute('aria-expanded', String(open));
  };
  if (t === change) return toggle(picker.hidden);
  if (t.getAttribute('aria-pressed') === 'true') return toggle(false);
  want = { from: Number(slot.dataset.bnGiftFrom) || 0, to: Number(t.dataset.bnGiftPick) };
  picking = true;
  mem.m = []; // sold-out minis get one more go, added together with the choice
  save();
  picker.querySelector('[data-bn-gift-error]')?.setAttribute('hidden', '');
  picker.setAttribute('aria-busy', 'true');
  t.setAttribute('data-bn-busy', '');
  picker.querySelectorAll('[aria-pressed]').forEach((c) => c.setAttribute('aria-pressed', String(c === t)));
  reconcile(last);
});

// Checkout waits for a pass in flight (3 s at most), so the order carries the gifts.
document.addEventListener(
  'submit',
  (e) => {
    const form = e.target;
    if (bypass || !running || form.id !== 'cart-form') return (bypass = false);
    e.preventDefault();
    const by = e.submitter;
    Promise.race([running, new Promise((r) => setTimeout(r, 3000))]).then(() => {
      bypass = true;
      form.requestSubmit(by?.form === form ? by : undefined);
    });
  },
  true
);

// La Découverte vouchers are BN- + 8 hexadecimal characters, so they never hold I, L or O: one typed that way
// (Neo 11-02: the e-mail's « 1 » read as « I ») is sent as 1 / 0. On window, so it runs before Horizon's
// document-level on:submit reads the field.
window.addEventListener(
  'submit',
  (e) => {
    const input = e.target instanceof HTMLFormElement ? e.target.querySelector('input[name="discount"]') : null;
    const m = input?.value.toUpperCase().replace(/\s+/g, '').match(/^BN-?([0-9A-FILO]{4})-?([0-9A-FILO]{4})$/);
    if (m) input.value = `BN-${m[1]}-${m[2]}`.replace(/[IL]/g, '1').replace(/O/g, '0');
  },
  true
);

hide();
if (D.k) {
  const go = () => (window.requestIdleCallback ? requestIdleCallback(() => reconcile(), { timeout: 1500 }) : reconcile());
  document.readyState === 'complete' ? go() : addEventListener('load', go, { once: true });
}

window.BN = window.BN || {};
window.BN.gift = { count, reconcile: () => reconcile() };
